import { Box, HStack, Icon, Spinner, Stack, Table, Tbody, Td, Text, Tfoot, Th, Thead, Tr, useToast } from '@chakra-ui/react'
import { faSort, faSortDown, faSortUp } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { flexRender, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type CellContext, type ColumnDef, type ColumnFiltersState, type ColumnSizingInfoState, type ColumnSizingState, type ExpandedState, type FilterFn, type OnChangeFn, type PaginationState, type Row, type SortingState } from '@tanstack/react-table'
import { EmptyTablePlaceholder, TableSkeleton } from 'BrokerTsLib/src/components'
import { yupResolver } from 'BrokerTsLib/src/rhf'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useFieldArray, useForm, type Control } from 'react-hook-form'
import * as yup from 'yup'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { diffFromOrigin } from '../EditableGrid'
import { EditableGridHeader } from './EditableGridHeader'
import { EditableGridPagination } from './EditableGridPagination'

interface EditableGridFormValues<TRow extends { id: any }> {
    root: TRow[]
}

// Backward-compatible alias — TKey param is kept for existing usages but ignored (TRow already has `id`)
export type EditableGridRow<TRow, _TKey = any> = TRow

type InternalEditableGridRow<TRow extends { id: any }> = TRow & {
    rowId: string | undefined
}

const getCustomSortedRowModel = <TData extends { id: any }>() => (table: any) => {
    const sortFn = getSortedRowModel<TData>()(table)
    return () => {
        const rowModel = sortFn()
        const newRows: Row<TData>[] = []
        const oldRows: Row<TData>[] = []
        for (const row of rowModel.rows) {
            const isNew = typeof row.original.id === 'number' ? row.original.id <= 0 : (row.original.id === '' || row.original.id == null || String(row.original.id).startsWith('new-'))
            if (isNew) newRows.push(row)
            else oldRows.push(row)
        }
        return {
            rows: [...newRows, ...oldRows],
            flatRows: [...newRows, ...oldRows],
            rowsById: rowModel.rowsById,
        }
    }
}

export interface EditableGridColumnContext<TRow extends { id: any }> extends CellContext<TRow, unknown> {
    control: Control<EditableGridFormValues<TRow>>
    updateRow: (index: number, row: Partial<TRow>) => void
    removeRow: (index: number) => void
}

export interface EditableGridHeaderContext<TRow extends { id: any }> {
    control: Control<EditableGridFormValues<TRow>>
    addRow: (row: TRow) => void
    rows: TRow[]
    displayedRows: TRow[]
}

export interface EditableGridColumnDef<TRow extends { id: any }> {
    id?: string
    accessorKey?: keyof TRow | string
    header: string | ((context: EditableGridColumnContext<TRow>) => React.ReactNode)
    cell: (context: EditableGridColumnContext<TRow>) => React.ReactNode
    enableSorting?: boolean
    filterFn?: FilterFn<any>
    enableColumnFilter?: boolean
    filterRender?: (props: { value: any, onChange: (value: any) => void }) => React.ReactNode
    size?: number
    minSize?: number
    maxSize?: number
    enableResizing?: boolean
}

export interface EditableGridExpandedRowRenderProps<TRow extends { id: any }> {
    row: Row<InternalEditableGridRow<TRow>>
    control: Control<EditableGridFormValues<TRow>>
}

export interface EditableGridProps<TRow extends { id: any }> {
    name?: string
    defaultValues: TRow[] | undefined
    schema?: yup.Schema<TRow>
    columnDefs: EditableGridColumnDef<TRow>[]
    normalizeRow: (row: TRow) => TRow
    newRowFactory?: () => TRow
    onCommitChanges?: (created: TRow[], edited: TRow[], deletedIds: TRow['id'][]) => Promise<void>
    isLoading?: boolean
    columnFilters?: ColumnFiltersState
    globalFilter?: string
    globalFilterFn?: FilterFn<any>
    headerItems?: (context: EditableGridHeaderContext<TRow>) => React.ReactNode
    isExpandable?: boolean
    expandedRowRender?: (props: EditableGridExpandedRowRenderProps<TRow>) => React.ReactNode
    level?: number
    isPaginated?: boolean
    defaultPageSize?: number
    pageSizeOptions?: number[]
    isSortable?: boolean
    initialSorting?: SortingState
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>
    enableColumnResizing?: boolean
}

export function EditableGrid<TRow extends { id: any }>({
    defaultValues,
    schema,
    columnDefs,
    normalizeRow,
    newRowFactory,
    onCommitChanges,
    isLoading,
    columnFilters,
    globalFilter,
    globalFilterFn,
    headerItems,
    isExpandable,
    expandedRowRender,
    level = 0,
    isPaginated = false,
    defaultPageSize = 10,
    pageSizeOptions,
    isSortable = true,
    initialSorting,
    onColumnFiltersChange,
    enableColumnResizing = false
}: EditableGridProps<TRow>) {
    const normalizedData = useMemo(() => {
        let tempIdCounter = -1
        return (defaultValues ?? []).map(d => {
            const row = normalizeRow(d)
            if (!row.id) {
                row.id = (tempIdCounter--) as unknown as TRow['id']
            }
            return row
        })
    }, [defaultValues, normalizeRow])

    const form = useForm<EditableGridFormValues<TRow>>({
        defaultValues: {
            root: normalizedData,
        } as any,
        resolver: schema ? yupResolver(yup
            .object()
            .shape({
                root: yup.array().of(schema).required(),
            }).required()
        ) : undefined
    })

    const { control, handleSubmit, formState: { isSubmitting, errors }, getValues, getFieldState, trigger } = form

    const originMapRef = useRef<Map<TRow['id'], TRow>>(new Map(normalizedData.map(r => [r.id, r])))

    const [expanded, setExpanded] = useState<ExpandedState>({})
    const [sorting, setSorting] = useState<SortingState>(initialSorting ?? [])
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
    const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>({} as ColumnSizingInfoState)
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: defaultPageSize,
    })

    const rowsEqual = useCallback((a: TRow, b: TRow) => {
        return JSON.stringify(normalizeRow(a)) === JSON.stringify(normalizeRow(b))
    }, [normalizeRow])

    const { prepend, update, remove, replace, fields } = useFieldArray<EditableGridFormValues<TRow>, any, 'rowId'>({
        control,
        name: 'root',
        keyName: 'rowId'
    })

    useEffect(() => {
        if (!isLoading) {
            const m = new Map<TRow['id'], TRow>()
            for (const r of normalizedData) m.set(r.id, r)
            originMapRef.current = m

            replace(normalizedData)

            setExpanded({})
        }
    }, [normalizedData, isLoading, replace])

    const addRow = useCallback((row: TRow) => {
        prepend(row, {})
    }, [prepend])

    const addEmptyRow = useCallback(() => {
        if (newRowFactory) {
            addRow(newRowFactory())
        }
    }, [addRow, newRowFactory])

    const toast = useToast()
    const { translate } = useTranslationsContext()

    const saveChanges = useCallback(async () => {
        const isValid = await trigger()
        if (!isValid) {
            toast({ status: 'error', title: translate('editableGrid.invalidInput', 'Invalid input.') })
            console.debug('Invalid input.', getFieldState('root').error, getValues().root)
            return
        }

        const data = getValues().root

        const { created, edited, deletedIds } = diffFromOrigin(originMapRef.current, data, rowsEqual)

        if (onCommitChanges) {
            console.debug('Saving changes:', { created, edited, deletedIds })

            try {
                await onCommitChanges(created, edited, deletedIds)
                toast({ status: 'success', title: translate('editableGrid.changesSaved', 'Changes saved') })
            } catch (e: any) {
                toast({ status: 'error', title: translate('editableGrid.saveFailed', 'Save failed'), description: String(e?.message ?? e) })
            }
        }
    }, [trigger, getValues, rowsEqual, onCommitChanges, toast, getFieldState])

    const cancelChanges = useCallback(() => {
        const originalValues = Array.from(originMapRef.current.values())

        replace(originalValues)

        toast({ status: 'info', title: translate('editableGrid.changesReverted', 'Changes reverted') })
    }, [replace, toast])

    const updateRow = useCallback((index: number, partial: Partial<TRow>) => {
        const current = getValues().root[index]
        update(index, { ...current, ...partial } as TRow)
    }, [update, getValues])

    const columns = useMemo(() => columnDefs.map(cd => ({
        ...cd,
        cell: (context: CellContext<TRow, unknown>) => cd.cell({ ...context, control, removeRow: remove, updateRow }),
        header: typeof cd.header === 'function'
            ? (context: CellContext<TRow, unknown>) =>
                (cd.header as (context: EditableGridColumnContext<TRow>) => React.ReactNode)({ ...context, control, removeRow: remove, updateRow: update })
            : cd.header,
        filterFn: cd.filterFn,
        enableColumnFilter: cd.enableColumnFilter ?? false,
        size: cd.size,
        minSize: cd.minSize,
        maxSize: cd.maxSize,
        enableResizing: cd.enableResizing,
    }) as unknown as ColumnDef<InternalEditableGridRow<TRow>, unknown>), [columnDefs, control, remove, update])

    const rows = fields as unknown as InternalEditableGridRow<TRow>[]

    const formTable = useReactTable<InternalEditableGridRow<TRow>>({
        columns,
        data: rows,
        state: {
            columnFilters, expanded, pagination, globalFilter, sorting,
            ...(enableColumnResizing ? { columnSizing, columnSizingInfo } : {}),
        },
        onColumnFiltersChange,
        onExpandedChange: setExpanded,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: isPaginated ? getPaginationRowModel() : undefined,
        getSortedRowModel: isSortable ? getCustomSortedRowModel<InternalEditableGridRow<TRow>>() : undefined,
        getRowId: (row) => row.rowId!,
        getExpandedRowModel: isExpandable ? getExpandedRowModel() : undefined,
        getRowCanExpand: () => !!isExpandable,
        ...(enableColumnResizing ? {
            columnResizeMode: 'onChange' as const,
            enableColumnResizing: true,
            onColumnSizingChange: setColumnSizing,
            onColumnSizingInfoChange: setColumnSizingInfo,
        } : {}),
    })

    useEffect(() => {
        if (!onColumnFiltersChange) {
            formTable.setColumnFilters(columnFilters ?? [])
        }
    }, [columnFilters, formTable, onColumnFiltersChange])

    const colCount = formTable.getVisibleLeafColumns().length
    const hasAnyFilter = columnDefs.some(cd => cd.enableColumnFilter && cd.filterRender)

    const grayShades = [
        'white',
        'neutral.50',
        'neutral.100',
        'neutral.200',
        'neutral.300',
        'neutral.400',
        'neutral.500',
        'neutral.600',
        'neutral.700',
        'neutral.800',
        'neutral.900',
    ]

    const displayedRows = formTable.getRowModel().rows

    return (
        <FormProvider {...form}>
            <Stack pos='relative' gap={0} border='1px solid' borderTopStyle={level > 0 ? 'dashed' : undefined} m={'-1px'} borderColor={grayShades[level + 1]} bg={grayShades[level]}>
                <EditableGridHeader
                    control={control}
                    originMap={originMapRef.current}
                    rowsEqual={rowsEqual}
                    addRow={addEmptyRow}
                    cancelChanges={cancelChanges}
                    isSubmitting={isSubmitting}
                    handleSubmit={handleSubmit(saveChanges)}
                    headerItems={headerItems?.({ control, addRow, rows, displayedRows: displayedRows.map(x => x.original) })}
                    level={level}
                    grayShades={grayShades}
                />
                <Box position='relative' overflowX={enableColumnResizing ? 'auto' : undefined}>
                    <Table size="sm" variant="simple" overflow='visible'
                        {...(enableColumnResizing ? { sx: { tableLayout: 'fixed', width: formTable.getCenterTotalSize() } } : {})}
                    >
                        <Thead>
                            {formTable.getHeaderGroups().map(hg => (
                                <Fragment key={hg.id}>
                                    <Tr>
                                        {hg.headers.map(h => (
                                            <Th key={h.id}
                                                cursor={h.column.getCanSort() ? 'pointer' : undefined}
                                                onClick={h.column.getToggleSortingHandler()}
                                                userSelect={h.column.getCanSort() || h.column.getCanResize() ? 'none' : undefined}
                                                title={
                                                    h.column.getCanSort()
                                                        ? h.column.getNextSortingOrder() === 'asc'
                                                            ? translate('editableGrid.sortAsc', 'Seřadit vzestupně')
                                                            : h.column.getNextSortingOrder() === 'desc'
                                                                ? translate('editableGrid.sortDesc', 'Seřadit sestupně')
                                                                : translate('editableGrid.sortClear', 'Zrušit řazení')
                                                        : undefined
                                                }
                                                bg={grayShades[level]}
                                                borderColor={grayShades[level + 1]}
                                                width={enableColumnResizing ? `${h.getSize()}px` : undefined}
                                                position={enableColumnResizing ? 'relative' : undefined}
                                            >
                                                <HStack gap={2} justifyContent='space-between'>
                                                    <Box>{flexRender(h.column.columnDef.header, h.getContext())}</Box>
                                                    {h.column.getCanSort() && (
                                                        <Icon
                                                            as={FontAwesomeIcon}
                                                            icon={
                                                                h.column.getIsSorted() === 'asc' ? faSortUp :
                                                                    h.column.getIsSorted() === 'desc' ? faSortDown :
                                                                        faSort
                                                            }
                                                            color={h.column.getIsSorted() ? 'gray.800' : 'gray.300'}
                                                        />
                                                    )}
                                                </HStack>
                                                {enableColumnResizing && h.column.getCanResize() && (
                                                    <Box
                                                        onMouseDown={h.getResizeHandler()}
                                                        onTouchStart={h.getResizeHandler()}
                                                        onClick={(e) => e.stopPropagation()}
                                                        position="absolute"
                                                        right={0}
                                                        top={0}
                                                        bottom={0}
                                                        width="4px"
                                                        cursor="col-resize"
                                                        userSelect="none"
                                                        bg={h.column.getIsResizing() ? 'blue.400' : 'transparent'}
                                                        _hover={{ bg: 'blue.200' }}
                                                        zIndex={1}
                                                    />
                                                )}
                                            </Th>
                                        ))}
                                    </Tr>
                                    {hasAnyFilter && (
                                        <Tr>
                                            {hg.headers.map(h => {
                                                const customDef = columnDefs.find(cd => (cd.id || cd.accessorKey) === (h.column.columnDef.id || (h.column.columnDef as any).accessorKey || h.column.id))
                                                return (
                                                    <Th key={h.id + '_filter'} p={1} bg={grayShades[level]} borderColor={grayShades[level + 1]}
                                                        width={enableColumnResizing ? `${h.getSize()}px` : undefined}
                                                    >
                                                        {customDef?.filterRender ? customDef.filterRender({
                                                            value: h.column.getFilterValue(),
                                                            onChange: (v) => h.column.setFilterValue(v)
                                                        }) : null}
                                                    </Th>
                                                )
                                            })}
                                        </Tr>
                                    )}
                                </Fragment>
                            ))}
                        </Thead>
                        <Tbody>
                            <TableSkeleton isLoaded={!isLoading} colSpan={colCount}>
                                <EmptyTablePlaceholder text={translate('editableGrid.noItems', 'Žádné položky')} colSpan={colCount}>
                                    {displayedRows.map(row => <Fragment key={row.id}>
                                        <Tr bg={row.getIsExpanded() ? grayShades[level + 1] : undefined}
                                            sx={{
                                                // zvyrazneni celeho radku pri hoveru nad akcnim tlacitkem (opt-in pres data-row-action)
                                                '&:has([data-row-action="delete"]:hover)': { bg: 'red.50' },
                                                '&:has([data-row-action="expand"]:hover)': { bg: 'blue.50' },
                                            }}
                                        >
                                            {row.getVisibleCells().map(cell => (
                                                <Td key={cell.id} borderBottom={row.getIsExpanded() ? 'none' : undefined}
                                                    width={enableColumnResizing ? `${cell.column.getSize()}px` : undefined}
                                                    overflow={enableColumnResizing ? 'hidden' : undefined}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </Td>
                                            ))}
                                        </Tr>
                                        {row.getIsExpanded() && (
                                            <Tr key={row.id + '_expanded'}>
                                                <Td colSpan={colCount} p={0} borderTop='none'>
                                                    {expandedRowRender ? expandedRowRender({ row, control }) : null}
                                                </Td>
                                            </Tr>
                                        )}
                                    </Fragment>
                                    )}
                                </EmptyTablePlaceholder>
                            </TableSkeleton>
                        </Tbody>
                        <Tfoot>
                            {errors.root && (
                                <Tr>
                                    <Td colSpan={colCount}>
                                        <Text color="red.500">{errors.root?.message}</Text>
                                    </Td>
                                </Tr>
                            )}
                            {isPaginated && (
                                <Tr>
                                    <Td colSpan={colCount}>
                                        <EditableGridPagination
                                            table={formTable}
                                            totalCount={rows.length}
                                            pageSizeOptions={pageSizeOptions}
                                        />
                                    </Td>
                                </Tr>
                            )}
                        </Tfoot>
                    </Table>
                    {isSubmitting &&
                        <Box pos='absolute' top={0} left={0} right={0} bottom={0} p={4} textAlign={'center'} bg={'rgba(0,0,0,0.15)'}>
                            <Spinner size='xl' p={8} m={8} />
                            <Text>Ukládání...</Text>
                        </Box>
                    }
                </Box>
            </Stack>
        </FormProvider>
    )
}
